import { css, useTheme } from '@emotion/react';

import { ReactNode, SyntheticEvent, useCallback, useEffect } from 'react';

import { useModal } from '@hooks/useModal';
import { Sizes, Spacing } from 'chatie-design-system';
import { Icon, VerticalEllipsis } from 'chatie-design-system/icon';

import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { CSSProps } from '@utils/misc';
import { useWebView } from '@utils/webview';

const ACTION_SHEET_MODAL_PREFIX = '__action-sheet';

/**
 * Components
 */
export type ActionSheetOption<T = string> = {
  label: string;
  value: T;
  isDanger?: boolean;
};

export type ActionSheetProps<T> = {
  name: string;
  options: ActionSheetOption<T>[];
  children?: (open: (e: SyntheticEvent) => void) => ReactNode;
  onSelect: (value: T) => void;
};

const ActionSheet = <T extends string>({
  name,
  options,
  children,
  onSelect,
}: ActionSheetProps<T>) => {
  const { web, listen } = useWebView();
  const { platform } = useGlobalContext();

  const actionSheetName = `${ACTION_SHEET_MODAL_PREFIX}-${name}`;
  const [, { open }] = useModal(actionSheetName, {
    routeChange: false,
    trigger: false,
  });

  const handleClick = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (platform === Platform.web) {
        open(options);
      } else {
        web.openActionSheet(actionSheetName, options);
      }
    },
    [platform, actionSheetName, options]
  );

  useEffect(() => {
    const unlisten = listen('actionSheetSelect', (e) => {
      const { name, value: index } = e.detail;

      if (name !== actionSheetName) {
        return;
      }

      const value = options[index] ? options[index].value : undefined;
      if (value) {
        onSelect(value);
      }
    });

    return unlisten;
  }, [options, onSelect]);

  return <>{children?.(handleClick)}</>;
};

export default ActionSheet;

/**
 * 케밥 형태
 */
const kebabMenuCSS = css`
  position: absolute;
  margin: 0;
  top: -${Spacing.medium}px;
  right: -${Spacing.medium}px;
  padding-top: ${Spacing.medium}px;
  padding-right: ${Spacing.medium}px;
  z-index: 1;
`;

export const KebabMenu = <T extends string>({
  className,
  ...props
}: ActionSheetProps<T> & CSSProps) => {
  const theme = useTheme();

  return (
    <ActionSheet {...props}>
      {(open) => (
        <aside className={className} css={kebabMenuCSS} onClick={open}>
          <Icon
            icon={VerticalEllipsis}
            color={theme.var.gray500}
            size={Sizes.small}
          />
        </aside>
      )}
    </ActionSheet>
  );
};
